/// <reference path="./index.ts" />

/**
 * @module types/invoices/types
 * @description Generic invoice provider type definitions for abstraction layer
 */

export namespace Invoices {
  export namespace Types {
    /**
     * @enum Providers
     * @description Supported invoice provider types
     */
    export enum Providers {
      BILLDU = 'billdu',
      INVOICED = 'invoiced',
    }

    /**
     * @const ProviderNames
     * @description Human-readable names for invoice providers
     */
    export const ProviderNames: Record<Providers, string> = {
      [Providers.BILLDU]: 'Billdu',
      [Providers.INVOICED]: 'Invoiced',
    };

    /**
     * @enum InvoiceStatus
     * @description Invoice status values used across all providers
     */
    export enum InvoiceStatus {
      DRAFT = 'draft',
      NOT_SENT = 'not_sent',
      SENT = 'sent',
      VIEWED = 'viewed',
      PAST_DUE = 'past_due',
      PENDING = 'pending',
      PAID = 'paid',
      PARTIAL = 'partial',
      VOIDED = 'voided',
      UNKNOWN = 'unknown',
    }

    /**
     * @const InvoiceStatusLabels
     * @description Human-readable labels for invoice statuses
     */
    export const InvoiceStatusLabels: Record<InvoiceStatus, string> = {
      [InvoiceStatus.DRAFT]: 'Draft',
      [InvoiceStatus.NOT_SENT]: 'Not Sent',
      [InvoiceStatus.SENT]: 'Sent',
      [InvoiceStatus.VIEWED]: 'Viewed',
      [InvoiceStatus.PAST_DUE]: 'Past Due',
      [InvoiceStatus.PENDING]: 'Pending',
      [InvoiceStatus.PAID]: 'Paid',
      [InvoiceStatus.PARTIAL]: 'Partial',
      [InvoiceStatus.VOIDED]: 'Voided',
      [InvoiceStatus.UNKNOWN]: 'Unknown',
    };

    /**
     * @interface InvoiceProviderConfig
     * @description Base configuration for invoice providers
     */
    export interface InvoiceProviderConfig {
      provider: Providers;
      webhookSecret?: string;
    }

    /**
     * @interface Client
     * @description Generic client/customer representation
     */
    export interface Client {
      id: string | number;
      email: string;
      name: string;
      company?: string;
      phone?: string;
      mobile?: string;
      street?: string;
      street2?: string;
      city?: string;
      province?: string;
      zip?: string;
      country?: string;
      fax?: string;
      website?: string;
      note?: string;
    }

    /**
     * @interface CartItemForInvoice
     * @description Cart item structure used when creating invoices
     */
    export interface CartItemForInvoice {
      filmTitle: string;
      filmId: string;
      title: string;
      price: number;
      itemCode?: string;
      platformId?: string;
    }

    /**
     * @interface LineItem
     * @description Line item for an invoice using Invoiced API naming conventions.
     * Field names match Invoiced API for direct pass-through to default provider.
     * @see https://developer.invoiced.com/api/invoices#line-item-object
     */
    export interface LineItem {
      name: string;
      unit_cost: number;
      quantity: number;
      unit?: string;
      catalog_item?: string;
      description?: string;
      metadata?: Record<string, unknown>;
    }

    /**
     * @interface InvoiceItem
     * @description Line item for an invoice
     */
    export interface InvoiceItem extends LineItem {}

    /**
     * @interface Invoice
     * @description Generic invoice representation with rich metadata from providers
     */
    export interface Invoice {
      id: string | number;
      number?: string;
      client: string | number;
      items: LineItem[];
      total?: number;
      currency?: string;
      status?: string;
      orderNumber?: string | number;
      discount?: number;
      discountType?: string;
      reference?: string;
      draft?: boolean;
      paid?: boolean;
      closed?: boolean;
      balance?: number;
      purchase_order?: string;
      url?: string;
      payment_url?: string;
      pdf_url?: string;
      created?: string;
      updated?: string;
      date?: string;
      notes?: string;
      metadata?: Record<string, unknown>;
    }

    /**
     * @interface ClientCreateRequest
     * @description Request data for creating a new client
     */
    export interface ClientCreateRequest {
      email: string;
      name: string;
      company: string;
      country: string;
      phone?: string;
      cell?: string;
      street?: string;
      street2?: string;
      city?: string;
      province?: string;
      zip?: string;
      fax?: string;
      website?: string;
      note?: string;
      companyId?: string;
      territory?: string;
    }

    /**
     * @interface ClientUpdateRequest
     * @description Request data for updating an existing client
     */
    export interface ClientUpdateRequest extends Partial<ClientCreateRequest> {
      // All fields from ClientCreateRequest are optional for updates
    }

    /**
     * @interface InvoiceCreateRequest
     * @description Request data for creating an invoice
     */
    export interface InvoiceCreateRequest {
      client: string | number;
      data?: Record<string, unknown>;
      note?: string;
      items: LineItem[];
      orderNumber: string | number;
      discount?: number;
      discountType?: 'percent' | 'amount';
      currency?: string;
    }

    /**
     * @interface ClientResponse
     * @description Response from client operations
     */
    export interface ClientResponse {
      data?: Client;
      success?: boolean;
      message?: string;
    }

    /**
     * @interface InvoiceResponse
     * @description Response from invoice operations
     */
    export interface InvoiceResponse {
      data?: Invoice;
      success?: boolean;
      message?: string;
    }

    /**
     * @interface SendInvoiceResponse
     * @description Response from sending an invoice
     */
    export interface SendInvoiceResponse {
      success: boolean;
      message?: string;
      data?: unknown;
    }

    /**
     * @interface InvoiceProviderMethods
     * @description Interface defining required methods for invoice providers
     */
    export interface InvoiceProviderMethods {
      init(): Promise<void>;
      getClient(clientId: string | number): Promise<Client>;
      createClient(data: ClientCreateRequest): Promise<Client>;
      updateClient(clientId: string | number, data: ClientUpdateRequest): Promise<Client>;
      createInvoice(request: InvoiceCreateRequest): Promise<Invoice>;
      sendInvoice(invoiceId: string | number, recipientEmail: string): Promise<SendInvoiceResponse>;
    }

    /**
     * @interface PrefillData
     * @description Pre-fill data for checkout form fields
     */
    export interface PrefillData {
      name?: string;
      company?: string;
      country?: string;
      companyId?: string;
      street?: string;
      street2?: string;
      city?: string;
      zip?: string;
      province?: string;
      territory?: string;
      phone?: string;
    }
  }
}
